import React, { useEffect, useState } from "react";
import { AlertTriangle, Calendar, ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "react-router-dom";
import EventStatusBadge from "@/components/EventStatusBadge";
import EventsFilterBar from "@/components/EventsFilterBar";
import useEventFilters from "@/lib/useEventFilters";
import { api } from "@/lib/api";

/**
 * Read-only "Événements à venir" panel showing logistics alerts.
 * Used by AdminOperations + AdminLogistics so non-commercial teams see the
 * alerts but cannot click through to edit. `readOnly=true` hides the link.
 *
 * Filter bar (search / date range / alert-only) + compact mode (top 6 with
 * "Voir tous" expansion) handled by the shared `useEventFilters` hook.
 */
export default function UpcomingEventsAlertPanel({ readOnly = false }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/events")
      .then((r) => setEvents(r.data))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  const upcoming = events.filter((e) => e.status === "upcoming" || e.status === "live");
  const alertedTotal = upcoming.filter((e) => e.logistics_alert).length;

  const f = useEventFilters(upcoming);

  return (
    <section data-testid="upcoming-events-panel" className="mt-2 mb-8">
      <div className="flex items-end justify-between flex-wrap gap-2 mb-3">
        <h2 className="font-display text-xl font-black text-[#2A0E3C]">
          Événements à venir
        </h2>
        {alertedTotal > 0 && (
          <div
            data-testid="upcoming-alert-banner"
            className="inline-flex items-center gap-2 bg-red-50 border border-red-300 text-red-700 px-3 py-1.5 rounded-full text-xs font-bold"
          >
            <span className="relative inline-flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-red-600" />
            </span>
            {alertedTotal} événement{alertedTotal > 1 ? "s" : ""} en alerte
          </div>
        )}
      </div>

      {upcoming.length > 0 && (
        <EventsFilterBar
          search={f.search} setSearch={f.setSearch}
          alertOnly={f.alertOnly} setAlertOnly={f.setAlertOnly}
          range={f.range} setRange={f.setRange}
          alertCount={alertedTotal}
          totalCount={upcoming.length}
          filteredCount={f.filtered.length}
          className="mb-4"
        />
      )}

      {loading ? (
        <p className="text-sm text-[#5C4664]">Chargement…</p>
      ) : upcoming.length === 0 ? (
        <p data-testid="upcoming-empty" className="text-sm text-[#5C4664]">Aucun événement à venir.</p>
      ) : f.filtered.length === 0 ? (
        <p data-testid="upcoming-no-match" className="text-sm text-[#5C4664]">
          Aucun événement ne correspond à ce filtre.
        </p>
      ) : (
        <>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {f.visible.map((ev) => {
              const alert = !!ev.logistics_alert;
              const content = (
                <div
                  data-testid={`upcoming-event-${ev.id}`}
                  data-alert={alert ? "true" : "false"}
                  className={`block bg-white rounded-2xl p-4 transition-colors ${
                    alert
                      ? "border-2 border-red-500 shadow-[0_0_0_4px_rgba(220,38,38,0.08)]"
                      : "border border-[#F2E2EC]"
                  }`}
                >
                  <div className="flex justify-between items-start gap-3 mb-1">
                    <h3 className="font-display text-base font-black text-[#2A0E3C] line-clamp-1">
                      {ev.title}
                    </h3>
                    <EventStatusBadge status={ev.status} />
                  </div>
                  <p className="text-xs text-[#5C4664]">{ev.host_names}</p>
                  {alert && (
                    <div
                      data-testid={`upcoming-event-${ev.id}-alert`}
                      className="mt-2.5 flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-red-700"
                    >
                      <AlertTriangle className="w-3 h-3 shrink-0" />
                      <span>
                        {ev.late_orders_count} cadeau{ev.late_orders_count > 1 ? "x" : ""} pas encore stocké{ev.late_orders_count > 1 ? "s" : ""} à J-48h
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-[11px] text-[#5C4664] mt-2.5">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {ev.event_date}
                    </span>
                    <span className="font-mono font-bold text-[#D81B8C]">{ev.code}</span>
                  </div>
                </div>
              );
              return (
                <li key={ev.id}>
                  {readOnly ? (
                    content
                  ) : (
                    <Link
                      to={`/admin/events/${ev.id}`}
                      className="block hover:opacity-90 transition-opacity"
                    >
                      {content}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>

          {f.hiddenCount > 0 && (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => f.setExpanded(true)}
                data-testid="upcoming-expand"
                className="inline-flex items-center gap-1.5 px-4 h-9 rounded-full bg-white border border-[#F2E2EC] text-sm font-semibold text-[#2A0E3C] hover:border-[#D81B8C]"
              >
                Voir tous ({f.filtered.length}) <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          )}
          {f.expanded && f.filtered.length > f.pageSize && (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => f.setExpanded(false)}
                data-testid="upcoming-collapse"
                className="inline-flex items-center gap-1.5 px-4 h-9 rounded-full text-sm font-semibold text-[#5C4664] hover:text-[#2A0E3C]"
              >
                Réduire <ChevronUp className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
